import React, { useState, useEffect, useRef } from 'react'
import { backendService } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './STLObjectManager.css'

// Constants for modal sizing
const DEFAULT_WIDTH = 750 // Increased from 600px
const MIN_WIDTH = 500
const MAX_WIDTH = 1200
const STORAGE_KEY = 'stl-object-manager-width'

interface STLObject {
  fileId: string
  originalName: string
  uploadDate: string
  metadata: {
    vertices: number
    faces: number
    size: { x: number; y: number; z: number }
  }
}

interface STLObjectManagerProps {
  projectId: string
  currentFileId?: string | null
  onClose: () => void
  onSelectSTL: (stlObject: STLObject) => void
}

export const STLObjectManager: React.FC<STLObjectManagerProps> = ({
  projectId,
  currentFileId,
  onClose,
  onSelectSTL
}) => {
  const [stlObjects, setStlObjects] = useState<STLObject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedObject, setSelectedObject] = useState<string | null>(null)

  // Modal width state and resize functionality
  const [modalWidth, setModalWidth] = useState<number>(() => {
    // Load saved width from localStorage or use default
    const savedWidth = localStorage.getItem(STORAGE_KEY)
    return savedWidth ? parseInt(savedWidth, 10) : DEFAULT_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const [resizeSide, setResizeSide] = useState<'left' | 'right'>('right')
  const modalRef = useRef<HTMLDivElement>(null)
  const resizeStartX = useRef<number>(0)
  const resizeStartWidth = useRef<number>(0)

  useEffect(() => {
    loadSTLObjects()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent, side: 'left' | 'right') => {
    e.preventDefault()
    setIsResizing(true)
    setResizeSide(side)
    resizeStartX.current = e.clientX
    resizeStartWidth.current = modalWidth
  }

  useEffect(() => {
    if (!isResizing) return

    // Add body class to prevent text selection during resize
    document.body.classList.add('resizing')

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartX.current
      let newWidth: number

      // Calculate new width based on which side is being dragged
      if (resizeSide === 'right') {
        // Dragging right edge: increase width when moving right
        newWidth = resizeStartWidth.current + deltaX
      } else {
        // Dragging left edge: increase width when moving left
        newWidth = resizeStartWidth.current - deltaX
      }

      // Clamp width between min and max
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth))

      setModalWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.classList.remove('resizing')
      // Save width to localStorage
      localStorage.setItem(STORAGE_KEY, modalWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.classList.remove('resizing')
    }
  }, [isResizing, modalWidth, resizeSide])

  const loadSTLObjects = async () => {
    setLoading(true)
    setError(null)

    try {
      logInfo(`Loading STL objects for project: ${projectId}`, 'ObjectManager')
      
      // Check if there's an STL_IN record for this project
      const stlInStatus = await backendService.getStlInStatus(projectId)
      
      if (stlInStatus.exists && stlInStatus.latest) {
        const stlObject: STLObject = {
          fileId: stlInStatus.latest.fileId,
          originalName: stlInStatus.latest.filename,
          uploadDate: stlInStatus.latest.uploadedAt || 'Unknown',
          metadata: {
            vertices: stlInStatus.latest.vertices,
            faces: stlInStatus.latest.faces,
            size: { x: 0, y: 0, z: 0 } // Size info not available in status response
          }
        }
        setStlObjects([stlObject])
        // Pre-select the currently loaded STL
        if (currentFileId && stlObject.fileId === currentFileId) {
          setSelectedObject(currentFileId)
        }
        logInfo(`Found ${1} STL object for project`, 'ObjectManager')
      } else {
        setStlObjects([])
        logInfo('No STL objects found for project', 'ObjectManager')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to load STL objects: ${errorMessage}`)
      logError(`STL objects load failed: ${errorMessage}`, 'ObjectManager')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectObject = (stlObject: STLObject) => {
    // Toggle selection: if already selected, deselect; otherwise select
    setSelectedObject(prev => (prev === stlObject.fileId ? null : stlObject.fileId))
  }

  const handleLoadSelected = async () => {
    if (!selectedObject) return

    const stlObject = stlObjects.find(obj => obj.fileId === selectedObject)
    if (!stlObject) return

    try {
      logInfo(`Loading selected STL object: ${stlObject.originalName}`, 'ObjectManager')
      onSelectSTL(stlObject)
      onClose()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to load STL object: ${errorMessage}`)
      logError(`STL object load failed: ${errorMessage}`, 'ObjectManager')
    }
  }

  const formatFileSize = (vertices: number, faces: number) => {
    const estimatedSize = (vertices * 12 + faces * 50) / 1024 // Rough estimate in KB
    if (estimatedSize < 1024) {
      return `~${estimatedSize.toFixed(0)} KB`
    } else {
      return `~${(estimatedSize / 1024).toFixed(1)} MB`
    }
  }

  return (
    <div className="stl-object-manager-overlay">
      <div
        ref={modalRef}
        className="stl-object-manager"
        style={{ width: `${modalWidth}px` }}
      >
        {/* Left resize handle */}
        <div
          className="resize-handle resize-handle-left"
          onMouseDown={(e) => handleResizeStart(e, 'left')}
        >
          <div className="resize-grip">⋮⋮</div>
        </div>

        {/* Right resize handle */}
        <div
          className="resize-handle resize-handle-right"
          onMouseDown={(e) => handleResizeStart(e, 'right')}
        >
          <div className="resize-grip">⋮⋮</div>
        </div>

        <div className="manager-header">
          <h3>STL Object Manager</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="manager-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading STL objects...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button onClick={loadSTLObjects}>Retry</button>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="objects-table-container">
                {stlObjects.length === 0 ? (
                  <div className="empty-state">
                    <p>No STL objects found for this project.</p>
                    <p>Upload an STL file in the STL Input View to get started.</p>
                  </div>
                ) : (
                  <table className="objects-table">
                    <thead>
                      <tr>
                        <th className="col-checkbox">
                          <div className="checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="header-checkbox"
                              checked={stlObjects.length > 0 && stlObjects.every(obj => obj.fileId === selectedObject)}
                              onChange={(e) => {
                                // Select all / Deselect all functionality
                                if (e.target.checked && stlObjects.length > 0) {
                                  handleSelectObject(stlObjects[0])
                                } else {
                                  setSelectedObject(null)
                                }
                              }}
                              title="Select/Deselect all"
                            />
                            <span className="checkbox-custom"></span>
                          </div>
                        </th>
                        <th className="col-name">STL Object</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stlObjects.map((obj, index) => (
                        <tr
                          key={obj.fileId}
                          className={`object-row stl-object-item ${selectedObject === obj.fileId ? 'selected' : ''} ${index % 2 === 0 ? 'even' : 'odd'}`}
                          onClick={() => handleSelectObject(obj)}
                        >
                          <td className="col-checkbox">
                            <div className="checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="object-checkbox"
                                checked={selectedObject === obj.fileId}
                                onChange={() => handleSelectObject(obj)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="checkbox-custom"></span>
                            </div>
                          </td>
                          <td className="col-name">
                            <div className="object-name-cell">
                              <div className="object-icon">📄</div>
                              <div className="object-details">
                                <div className="object-filename">{obj.originalName}</div>
                                <div className="object-metadata">
                                  <span className="meta-item">📊 {obj.metadata.vertices.toLocaleString()} vertices</span>
                                  <span className="meta-separator">•</span>
                                  <span className="meta-item">🔺 {obj.metadata.faces.toLocaleString()} faces</span>
                                  <span className="meta-separator">•</span>
                                  <span className="meta-item">💾 {formatFileSize(obj.metadata.vertices, obj.metadata.faces)}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="col-actions">
                            <button
                              className="delete-button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (window.confirm(`Are you sure you want to delete "${obj.originalName}"?`)) {
                                  // TODO: Implement delete functionality
                                  logInfo(`Delete requested for: ${obj.fileId}`)
                                }
                              }}
                              title="Delete STL object"
                            >
                              <span className="delete-icon">🗑️</span>
                              <span className="delete-text">Delete</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="manager-actions">
                <button
                  className="load-btn"
                  onClick={handleLoadSelected}
                  disabled={!selectedObject}
                >
                  Load Selected STL
                </button>
                <button className="cancel-btn" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
